import type { ReactNode } from 'react';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { PropertyCartItem, ServiceInfo } from '../types/cart.types';
import { isFreeFloorPlanLine, type FloorPlanTier } from '../floor-plan/floorPlanOffer';
import { FloorPlanServiceLine } from '../floor-plan/FloorPlanServiceLine';

type ServiceCellProps = {
  services: ServiceInfo[] | null;
  item: PropertyCartItem;
  onEdit?: (item: PropertyCartItem) => void;
  readOnly?: boolean;
  /**
   * DV-3204: the free Basic Floor Plan this property's order will get. The
   * cart has no line for it (order-service adds it at creation), so it is
   * shown from here; a placed order's own line is shown without it.
   */
  freeFloorPlan?: FloorPlanTier | null;
};

const ServiceCell = ({
  services,
  item,
  onEdit,
  readOnly = false,
  freeFloorPlan = null,
}: ServiceCellProps) => {
  if (!services || services.length === 0) {
    if (readOnly) {
      return <Typography variant="body">-</Typography>;
    }
    return (
      <Button
        type="button"
        variant="link"
        // Standalone link-styled action, so it carries the 44px touch floor
        // itself — see button.tsx's link-variant comment.
        className="px-0 font-normal text-primary underline max-lg:min-h-11"
        onClick={() => onEdit?.(item)}
      >
        Select services
      </Button>
    );
  }

  const editLabel = `Edit services for ${item.formattedAddress || 'property'}`;
  // A placed order carries the free line itself; it is not a photo service,
  // so it gets its own line rather than a "1 photos" count. Every such line
  // is shown. In the cart, where the order does not exist yet, the line it
  // will get is shown instead.
  const photoServices = services.filter(
    (service) => !isFreeFloorPlanLine(service),
  );
  const orderFloorPlanLines = services
    .filter(isFreeFloorPlanLine)
    .filter((line) => Boolean(line.name));
  const cartFloorPlanLines = freeFloorPlan ? [freeFloorPlan] : [];
  const floorPlanLines =
    orderFloorPlanLines.length > 0 ? orderFloorPlanLines : cartFloorPlanLines;

  const rows: { key: string; content: ReactNode }[] = [
    ...photoServices.map((service, index) => ({
      key: service.lineItemUUID || `service-${index}`,
      content: (
        <div className="flex items-center gap-3">
          {service.onlineImage && (
            <img
              src={service.onlineImage}
              height={64}
              width={98}
              alt=""
              className="shrink-0 rounded-md bg-muted object-cover"
            />
          )}
          <div className="flex flex-1 flex-col gap-1">
            <Typography variant="body">{service.name}</Typography>
            <Typography variant="body-semibold">
              {service.quantity || 0} photos
            </Typography>
          </div>
        </div>
      ),
    })),
    ...floorPlanLines.map((line, index) => ({
      key: `floor-plan-${index}`,
      content: (
        <FloorPlanServiceLine name={line.name} onlineImage={line.onlineImage} />
      ),
    })),
  ];

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-1 flex-col">
        {rows.map((row, index) => (
          <div key={row.key}>
            {row.content}
            {index < rows.length - 1 && (
              <div className="my-2 h-px w-full bg-border" />
            )}
          </div>
        ))}
      </div>
      {!readOnly && onEdit && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground hover:text-primary"
          aria-label={editLabel}
          onClick={() => onEdit(item)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default ServiceCell;
